import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import { withLocation } from 'utils/hocs';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import { ProductMain } from '../components/Ecommerce/Product';
import {
  productState,
  staticProductState,
  variantState,
} from '../utils/recoil/product';
import {
  formatContentfulComponentProps,
  returnLocalisedPath,
  updateUrlParams,
} from '../utils/globals';
import { MicroDataHelmet } from '../components/Utils/MicroData/Utils/microDataHelmet';
import { MainLayout } from '../components/UI/Layout/Main/mainLayout';
import { Component } from '../components/Contentful/component';
import { productMicroData } from '../components/Utils/MicroData/Product/productMicroData';

export default withLocation(({ data, search, pageContext }) => {
  const page = data?.contentfulPage;
  const { allLocalSlugs } = pageContext;
  const locale = data?.contentfulPage?.product?.node_locale.toLowerCase();
  const siteUrl = data?.site?.siteMetadata?.siteUrl;

  const {
    components,
    layout,
    popup,
    seoMetadata,
    slug,
    title,
    canonical,
    robots,
  } = page;

  const productUrl = `${siteUrl}/${
    process.env.GATSBY_ZONE
  }${returnLocalisedPath(locale, slug?.url || '')}`;

  const setProduct = useSetRecoilState(productState);
  const setStaticProduct = useSetRecoilState(staticProductState);

  const product = useRecoilValue(productState);
  const [variant, setVariant] = useRecoilState(variantState);

  useEffect(() => {
    const productToSet = data?.contentfulProduct;

    setProduct(productToSet);
    setStaticProduct(productToSet);

    // search params initialisation
    if (typeof window !== 'undefined') {
      // set default option
      if (
        search.option &&
        JSON.parse(search.option) &&
        productToSet.variants[0] &&
        productToSet.variants[0].option
      ) {
        setProduct({
          ...productToSet,
          ...productToSet.variants[0].option,
          isOptionCheck: true,
        });
      }

      // Set default color if color url params
      if (search.color) {
        let variants = [...productToSet.variants];
        if (productToSet.limitedVariants) {
          variants = [...variants, ...productToSet.limitedVariants];
        }

        const variantToSet = variants.find(
          (p) => p.color?.shopifyKey.toLowerCase() === search.color
        );

        if (variantToSet) {
          setVariant(variantToSet);
        }
      } else if (productToSet.variants) {
        // OR default to first variant
        const firstVariant = productToSet.variants[0];
        setVariant(firstVariant);
        // handle urlParams
        if (firstVariant.color) {
          updateUrlParams('color', firstVariant.color.shopifyKey.toLowerCase());
        }
        if (firstVariant.denominations) {
          updateUrlParams(
            'denomination',
            firstVariant.denominations.toLowerCase()
          );
        }
      }

      // set default size
      if (search.size) {
        const variantsToSet = productToSet.variants.filter(
          (p) => p?.size?.value === search?.size
        );

        if (variantsToSet && variantsToSet[0]) setVariant(variantsToSet[0]);
      }

      if (search.diopter) {
        setVariant((prev) =>
          prev
            ? {
                ...prev,
                diopter: search.diopter,
              }
            : undefined
        );
      }
    }
  }, []);

  // hook to reset to default color if current variant is not included in product variant list
  // useful on option unselection
  useEffect(() => {
    if (
      product?.variants &&
      !product?.variants.find((v) => v?.color?.hex === variant?.color?.hex)
    ) {
      setVariant(product.variants[0]);
    }
  }, [product?.variants]);

  const staticProductData = data?.contentfulProduct;
  const statefulProductData = product;
  const staticVariantData = staticProductData.variants[0];
  const statefulVariantData = variant;
  const productData = statefulProductData || staticProductData;
  const variantData = statefulVariantData || staticVariantData;

  // SEO Micro Data
  const returnProductMicroData = productMicroData(
    productUrl,
    productData,
    page
  );

  return (
    <>
      <MicroDataHelmet schemaOrgJSONLD={returnProductMicroData} />
      <MainLayout
        {...layout}
        page={{ seoMetadata, slug, title, canonical, robots, allLocalSlugs }}
        popup={popup}
        isProduct={true}>
        {components &&
          components.map((component) => {
            if (component.key === 'ProductMain') {
              return (
                <ProductMain
                  product={productData}
                  variant={variantData}
                  {...formatContentfulComponentProps(component)}
                />
              );
            }
            return <Component {...component} />;
          })}
      </MainLayout>
    </>
  );
});

export const pageQuery = graphql`
  query ProductPageById($id: String!, $productId: String!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    contentfulProduct(id: { eq: $productId }) {
      ...ProductData
      recommendations {
        ...GridData
      }
    }
    contentfulPage(id: { eq: $id }) {
      ...PageData
    }
  }
`;
